// Reporting trend chart — PURE shaper + metric catalogue (shadcn charts).
//
// The reporting page fixes currency globally (the display-currency toggle), so a
// chart is ALWAYS a single series. This module:
//   - defines the metric CATALOGUE (label + format + tone + a value() computed
//     from summed base totals, so money-RATIOS recompute from the summed
//     numerator/denominator — never an averaged daily ratio);
//   - buckets the daily trend into daily / weekly / monthly buckets (pure), each
//     bucket summing the additive base totals;
//   - shapes a bucketed metric into Recharts-ready rows ({ bucket, value }) over
//     the FULL continuous calendar domain of the range (one slot per bucket),
//     left-joining the data on so EMPTY buckets stay visible in position (never
//     collapsed) — filled by metric kind: additive → 0, ratio → null (a gap).
// The same value() feeds both the hero-card headline (whole-range totals) and the
// per-bucket chart points, so the two surfaces can never disagree. Every chart on
// the page (hero minis + the big over-time chart) reads this one shaper, so the
// continuous-domain fill can't diverge between them.

import { formatReportingAmount } from '../../lib/reportingMoney';
import type { CurrencyAmount, ReportingSummary, ReportingTrendPoint } from '../../types/reporting';
import {
  bucketIdFor,
  DEFAULT_TREND_GROUPING,
  enumerateBucketDomain,
  type TrendGrouping,
} from './chartDate';

// money → currency-formatted; count → integer; ratio → percentage.
export type TrendMetricFormat = 'money' | 'count' | 'ratio';
// Which direction is "good" (drives the delta colour). Spend is neutral.
export type MetricTone = 'good-up' | 'good-down' | 'neutral';
// How an EMPTY bucket is filled (see buildTrendSeries): additive metrics (spend,
// leads, impressions, …) → 0 (a genuine no-activity bucket is a zero bar); ratio
// metrics (cost-per-lead, cpm, ctr, …) → null (a $0 CPM is misleading — render a
// gap, no bar). NOTE: `format` alone can't decide this — spend is money+additive
// while cpl is money+ratio — so kind is stored explicitly.
export type MetricKind = 'additive' | 'ratio';

// Additive base totals for a bucket / the whole range — the raw sums that money
// ratios recompute from (per the DDL do-NOT-SUM-a-ratio rule).
export interface TrendTotals {
  spend: number;
  metaLeadSpend: number; // §4 — on-Meta form-lead campaign spend only
  leads: number;
  metaLeads: number;
  websiteLeads: number;
  impressions: number;
  linkClicks: number;
}

export interface TrendMetric {
  key: string;
  label: string;
  format: TrendMetricFormat;
  tone: MetricTone;
  // Additive vs ratio — drives empty-bucket gap-fill (0 vs null). See MetricKind.
  kind: MetricKind;
  // The metric value from SUMMED totals. Additive metrics read one field; ratios
  // divide summed numerator by summed denominator (× scale), so bucketing +
  // whole-range aggregation are automatically correct.
  value: (t: TrendTotals) => number;
}

const ratio = (num: number, den: number): number => (den > 0 ? num / den : 0);

// The chart metric catalogue — every metric derivable from the /summary trend
// (no server change). The first 8 are the original hero stats; the tail (link
// clicks, cost-per-link-click, CPM) are extra derivable charts the selector can
// add. (Metrics needing base columns the summary trend doesn't carry — all-click
// CTR/CPC, landing-page views, engagement — await a summary-trend extension.)
export const CHART_METRICS: TrendMetric[] = [
  { key: 'spend', label: 'spend', format: 'money', tone: 'neutral', kind: 'additive', value: (t) => t.spend },
  { key: 'leads', label: 'leads', format: 'count', tone: 'good-up', kind: 'additive', value: (t) => t.leads },
  { key: 'metaLeads', label: 'meta leads', format: 'count', tone: 'good-up', kind: 'additive', value: (t) => t.metaLeads },
  { key: 'websiteLeads', label: 'website leads', format: 'count', tone: 'good-up', kind: 'additive', value: (t) => t.websiteLeads },
  { key: 'cpl', label: 'cost per lead', format: 'money', tone: 'good-down', kind: 'ratio', value: (t) => ratio(t.spend, t.leads) },
  { key: 'costPerMetaLead', label: 'cost per meta lead', format: 'money', tone: 'good-down', kind: 'ratio', value: (t) => ratio(t.metaLeadSpend, t.metaLeads) },
  { key: 'impressions', label: 'impressions', format: 'count', tone: 'good-up', kind: 'additive', value: (t) => t.impressions },
  { key: 'ctr', label: 'link ctr', format: 'ratio', tone: 'good-up', kind: 'ratio', value: (t) => ratio(t.linkClicks, t.impressions) * 100 },
  { key: 'linkClicks', label: 'link clicks', format: 'count', tone: 'good-up', kind: 'additive', value: (t) => t.linkClicks },
  { key: 'costPerLinkClick', label: 'cost per link click', format: 'money', tone: 'good-down', kind: 'ratio', value: (t) => ratio(t.spend, t.linkClicks) },
  { key: 'cpm', label: 'cpm', format: 'money', tone: 'good-down', kind: 'ratio', value: (t) => ratio(t.spend, t.impressions) * 1000 },
];

// Default cards = the original 8 hero stats (in catalogue order).
export const DEFAULT_CHART_METRIC_KEYS: string[] = [
  'spend',
  'leads',
  'metaLeads',
  'websiteLeads',
  'cpl',
  'costPerMetaLead',
  'impressions',
  'ctr',
];

export const DEFAULT_TREND_METRIC = 'spend';

const METRIC_BY_KEY = new Map<string, TrendMetric>(CHART_METRICS.map((m) => [m.key, m]));

export function isTrendMetricKey(key: string): boolean {
  return METRIC_BY_KEY.has(key);
}

// Validate/resolve a metric key, falling back to the default for an unknown/stale
// key (a removed metric, a bad restore).
export function resolveTrendMetric(key: string | null | undefined): TrendMetric {
  return (key ? METRIC_BY_KEY.get(key) : undefined) ?? METRIC_BY_KEY.get(DEFAULT_TREND_METRIC)!;
}

const r2 = (n: number): number => Math.round(n * 100) / 100;

const sumCurrency = (arr: CurrencyAmount[] | undefined): number =>
  (arr ?? []).reduce((s, c) => s + c.amount, 0);

const ZERO_TOTALS: TrendTotals = {
  spend: 0,
  metaLeadSpend: 0,
  leads: 0,
  metaLeads: 0,
  websiteLeads: 0,
  impressions: 0,
  linkClicks: 0,
};

// One trend point → its additive totals. The point is display-currency-scoped, so
// the scalar fields already hold that currency's values and spend/metaLeadSpend
// come from the (single-entry) per-currency arrays.
export function pointTotals(pt: ReportingTrendPoint): TrendTotals {
  return {
    spend: sumCurrency(pt.spend),
    metaLeadSpend: sumCurrency(pt.metaLeadSpend),
    leads: pt.leads,
    metaLeads: pt.metaLeads,
    websiteLeads: pt.websiteLeads,
    impressions: pt.impressions,
    linkClicks: pt.linkClicks,
  };
}

function addInto(acc: TrendTotals, t: TrendTotals): void {
  acc.spend += t.spend;
  acc.metaLeadSpend += t.metaLeadSpend;
  acc.leads += t.leads;
  acc.metaLeads += t.metaLeads;
  acc.websiteLeads += t.websiteLeads;
  acc.impressions += t.impressions;
  acc.linkClicks += t.linkClicks;
}

// Whole-range totals (drives the hero-card headline value + delta).
export function sumAllTotals(trend: ReportingTrendPoint[]): TrendTotals {
  const acc: TrendTotals = { ...ZERO_TOTALS };
  for (const pt of trend) addInto(acc, pointTotals(pt));
  return acc;
}

export interface TrendBucket {
  bucket: string; // the bucket id (see chartDate.bucketIdFor)
  totals: TrendTotals;
}

// PURE bucketing helper — re-buckets the daily trend into daily / weekly / monthly
// buckets, SUMMING the additive base totals per bucket. daily is a passthrough
// (one bucket per day). Ratios are NOT summed here; they're recomputed by the
// metric value() from each bucket's summed totals. Sorted ascending by bucket id
// (ISO dates + 'YYYY-MM' both sort lexicographically).
export function bucketTrend(trend: ReportingTrendPoint[], grouping: TrendGrouping): TrendBucket[] {
  const byBucket = new Map<string, TrendTotals>();
  for (const pt of trend) {
    const id = bucketIdFor(pt.date, grouping);
    let acc = byBucket.get(id);
    if (!acc) {
      acc = { ...ZERO_TOTALS };
      byBucket.set(id, acc);
    }
    addInto(acc, pointTotals(pt));
  }
  return [...byBucket.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([bucket, totals]) => ({ bucket, totals }));
}

// A chart row: the bucket id (X category) + its value. `value` is `null` for an
// EMPTY ratio bucket (a true gap — recharts draws no bar/breaks the line there);
// additive empties are `0` (a zero-height bar in position). Every calendar bucket
// across the range gets a row (continuous domain), so gaps are never collapsed.
export interface TrendChartRow {
  bucket: string;
  value: number | null;
}

export interface TrendChartData {
  metric: TrendMetric;
  format: TrendMetricFormat;
  currency: string | null; // display currency (money formatting); null for count/ratio
  grouping: TrendGrouping;
  rows: TrendChartRow[];
  isEmpty: boolean;
}

// The COMPLETE, chronological bucket domain for a range + grouping, unioned with
// any data bucket that (defensively) falls outside the range so no server row is
// ever dropped. This is the ONE fill every chart on the page shares (hero minis +
// the big over-time chart), so none can diverge in which buckets it draws.
function fullBucketDomain(
  range: { from: string; to: string } | undefined,
  buckets: TrendBucket[],
  grouping: TrendGrouping,
): string[] {
  const domain = range ? enumerateBucketDomain(range.from, range.to, grouping) : [];
  const seen = new Set(domain);
  const merged = [...domain];
  for (const b of buckets) {
    if (!seen.has(b.bucket)) {
      seen.add(b.bucket);
      merged.push(b.bucket);
    }
  }
  // bucket ids (ISO dates + 'YYYY-MM') sort chronologically lexicographically.
  return merged.sort((a, b) => a.localeCompare(b));
}

// Shape the (scoped) summary trend into single-series chart data for a metric at
// the given grouping. Currency is global, so exactly one series. The series spans
// the FULL calendar domain of the summary's range (one slot per bucket), with the
// server's per-bucket rows LEFT-JOINED on: an empty bucket is filled by metric
// kind — additive → 0, ratio → null — so gaps stay visible in position and are
// never collapsed. `isEmpty` reflects whether there's any real data (an all-empty
// range yields no rows, so the chart shows its "no data" state rather than a wall
// of zero bars).
export function buildTrendSeries(
  summary: ReportingSummary,
  metricKey: string,
  grouping: TrendGrouping = DEFAULT_TREND_GROUPING,
): TrendChartData {
  const metric = resolveTrendMetric(metricKey);
  const buckets = bucketTrend(summary.trend ?? [], grouping);
  const currency = metric.format === 'money' ? (summary.currencies[0] ?? null) : null;
  if (buckets.length === 0) {
    return { metric, format: metric.format, currency, grouping, rows: [], isEmpty: true };
  }
  const totalsByBucket = new Map(buckets.map((b) => [b.bucket, b.totals]));
  const gapValue: number | null = metric.kind === 'ratio' ? null : 0;
  const rows: TrendChartRow[] = fullBucketDomain(summary.range, buckets, grouping).map((bucket) => {
    const totals = totalsByBucket.get(bucket);
    return { bucket, value: totals ? r2(metric.value(totals)) : gapValue };
  });
  return { metric, format: metric.format, currency, grouping, rows, isEmpty: false };
}

// Format one metric value for display (headline + tooltip). money → currency
// amount; count → integer; ratio → percentage.
export function formatMetricValue(
  format: TrendMetricFormat,
  value: number,
  currency: string | null,
): string {
  if (format === 'money') return formatReportingAmount(value, currency ?? '');
  if (format === 'ratio') return `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
  return Math.round(value).toLocaleString('en-US');
}
